import * as React from "react";
import { styled } from "@mui/material/styles";
import { useSelector  } from "react-redux";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import { CircularProgress, Popper, Typography } from "@mui/material";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import theme, { SEARCH_BUTTON_COLOR } from "../../../theme";
import Button from "../../ui/Button";
import Modal from '@mui/material/Modal';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { getlinkingVehiclesByEngineCode, getlinkingVehiclesByTypeApproval, getlinkingVehiclesByVIN, searchByPlate } from "../../../services/tecdocService";
import {LinkingTargetState } from "../../../store/reducers/linkingTargetReducer";
import { VehicleDetail } from "../../../interfaces/VehicleDetail";
import { isPlateDisambiguationResponse, PlateDisambiguationOption } from "../../../interfaces/PlateSearch";

const style = {
  position: 'absolute' as 'absolute',
  top: '40%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  height: '400px',
  width: '90%',
  bgcolor: 'background.paper',
  border: '2px solid #f1f1f1',
  boxShadow: 24,
  p: 4,
  overflow: 'hidden',
};

const Item = styled(Paper)(({ theme }) => ({
  boxShadow: "none !important",
  borderRadius: 0,
  backgroundColor: "#F1F1F1 !important",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.primary,
}));

interface SearchType {
  label: string;
  number: number;
}

interface CantonOption {
  label: string;
  code: string;
}

function sleep(duration: number): Promise<void> {
  return new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve();
    }, duration);
  });
}

type VehicleModalSource = 'vin' | 'engine';
type PlateModalStep = 'disambiguation' | 'vehicles';

const VehicleIdentification = () => {
  const [openSearchType, setOpenSearchType] = React.useState(false);
  const linkingTargetType = useSelector((state: { linkingTarget: LinkingTargetState}) => state.linkingTarget.linkingTargetType);
  const [selectedSearchType, setSelectedSearchType] = React.useState<SearchType>({
    label: " Typengenehmigung",
    number: 3,
  });
  const [input, setInput] = React.useState("");
  const [linkageVehiclesByCode, setLinkageVehiclesByCode] = React.useState<VehicleDetail[] | null>(null);
  const [openCantonSelect, setOpenCantonSelect] = React.useState(false);
  const [selectedCanton, setSelectedCanton] = React.useState<CantonOption | null>(null);
  const [plateNumber, setPlateNumber] = React.useState("");
  const [plateDisambiguationOptions, setPlateDisambiguationOptions] = React.useState<PlateDisambiguationOption[]>([]);
  const [openPlateModal, setOpenPlateModal] = React.useState(false);
  const [plateModalStep, setPlateModalStep] = React.useState<PlateModalStep>('disambiguation');
  const [plateModalVehicles, setPlateModalVehicles] = React.useState<VehicleDetail[]>([]);
  const [pendingPlateSearch, setPendingPlateSearch] = React.useState<{ canton: string; number: string } | null>(null);

  const [options, setOptions] = React.useState<readonly SearchType[]>([]);
  const loadingSearchType = openSearchType && options.length === 0;

  const [errorVin, setErrorVin] = React.useState('');
  const [errorPlate, setErrorPlate] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [vehicleModalSource, setVehicleModalSource] = React.useState<VehicleModalSource>('engine');
  const [resolvingVehicle, setResolvingVehicle] = React.useState(false);
  const handleClose = () => {
    setOpen(false);
  };
  const handleClosePlateModal = () => {
    setOpenPlateModal(false);
    setPlateDisambiguationOptions([]);
    setPlateModalVehicles([]);
    setPlateModalStep('disambiguation');
    setPendingPlateSearch(null);
  };

  // Autocomplete for Search by VIN number, Engine code
  React.useEffect(() => {
    let activeSearchType = true;

    if (!loadingSearchType) {
      return undefined;
    }

    (async () => {
      await sleep(1e3); // For demo purposes.

      if (activeSearchType) {
        setOptions([...searchTypes]);
      }
    })();

    return () => {
      activeSearchType = false;
    };
  }, [loadingSearchType]);

  React.useEffect(() => {
    if (!openSearchType) {
      setOptions([]);
    }
  }, [openSearchType]);

  const hanldeSearchTypeChange = (event: React.ChangeEvent<{}>, searchType: SearchType | null) => {
    if (searchType){
      setSelectedSearchType(searchType);
    }
  }

  const navigateToAssemblies = (vehicle: VehicleDetail) => {
    const url = `/assemblies?linkageTargetId=${vehicle.linkageTargetId}&linkageTargetType=${vehicle.linkageTargetType || linkingTargetType}`;
    window.location.href = url;
  };

  const openVehicleResultsModal = (vehicles: VehicleDetail[], source: VehicleModalSource) => {
    setOpenSearchType(false);
    setLinkageVehiclesByCode(vehicles);
    setVehicleModalSource(source);
    setOpen(true);
    setErrorVin('');
  };

  const getSearchInputLabel = () => {
    if (selectedSearchType.number === 1) return "VIN eingeben";
    if (selectedSearchType.number === 2) return "Motornummer eingeben";
    return "Typengenehmigung eingeben";
  };

  const getSearchTypeLabel = () => {
    if (selectedSearchType.number === 1) return "Nach VIN";
    if (selectedSearchType.number === 2) return "Nach Motornummer";
    return "Nach Typengenehmigung";
  };

  const getSearchErrorMessage = () => {
    if (selectedSearchType.number === 1) return "VIN wurde nicht gefunden!";
    if (selectedSearchType.number === 2) return "Motornummer wurde nicht gefunden!";
    return "Typengenehmigung wurde nicht gefunden!";
  };

  const handleSearchType = async () => {
   if (!input || !selectedSearchType) {
     return;
   }

   try {
    let linkageVehicles: VehicleDetail[] = [];

    if (selectedSearchType.number === 1) {
      linkageVehicles = await getlinkingVehiclesByVIN(input);
    } else if (selectedSearchType.number === 2) {
      linkageVehicles = await getlinkingVehiclesByEngineCode(input);
    } else if (selectedSearchType.number === 3) {
      linkageVehicles = await getlinkingVehiclesByTypeApproval(input);
    }

    if (linkageVehicles?.length) {
      if (selectedSearchType.number === 3) {
        navigateToAssemblies(linkageVehicles[0]);
        setErrorVin('');
      } else {
        const source: VehicleModalSource = selectedSearchType.number === 1 ? 'vin' : 'engine';
        openVehicleResultsModal(linkageVehicles, source);
      }
    } else {
      setErrorVin(getSearchErrorMessage());
    }
   } catch (error) {
    setErrorVin(getSearchErrorMessage());
   }
  }

  const handleVehicleRowClick = async (vehicle: VehicleDetail) => {
    if (vehicleModalSource === 'vin') {
      const typeApproval = vehicle.typeApproval;
      if (!typeApproval) {
        setErrorVin("Typengenehmigung wurde nicht gefunden!");
        return;
      }

      setResolvingVehicle(true);
      try {
        const resolved = await getlinkingVehiclesByTypeApproval(typeApproval);
        if (!resolved?.length) {
          setErrorVin("VIN wurde nicht gefunden!");
          return;
        }

        if (resolved.length === 1 && resolved[0].linkageTargetId) {
          setOpen(false);
          navigateToAssemblies(resolved[0]);
          setErrorVin('');
        } else {
          setLinkageVehiclesByCode(resolved);
          setVehicleModalSource('engine');
          setErrorVin('');
        }
      } catch {
        setErrorVin("VIN wurde nicht gefunden!");
      } finally {
        setResolvingVehicle(false);
      }
      return;
    }

    if (!vehicle.linkageTargetId) {
      setErrorVin(getSearchErrorMessage());
      return;
    }

    setOpen(false);
    navigateToAssemblies(vehicle);
    setErrorVin('');
  };

  const handlePlateVehicleRowClick = async (vehicle: VehicleDetail) => {
    const typeApproval = vehicle.typeApproval;
    if (!typeApproval) {
      setErrorPlate("Typengenehmigung wurde nicht gefunden!");
      return;
    }

    setResolvingVehicle(true);
    try {
      const resolved = await getlinkingVehiclesByTypeApproval(typeApproval);
      if (resolved?.[0]?.linkageTargetId) {
        handleClosePlateModal();
        navigateToAssemblies(resolved[0]);
        setErrorPlate('');
      } else {
        setErrorPlate("Kennzeichen wurde nicht gefunden!");
      }
    } catch {
      setErrorPlate("Kennzeichen wurde nicht gefunden!");
    } finally {
      setResolvingVehicle(false);
    }
  };

  const showPlateVehiclesModal = (vehicles: VehicleDetail[]) => {
    if (!vehicles.length) {
      setErrorPlate("Kennzeichen wurde nicht gefunden!");
      return;
    }
    setPlateModalVehicles(vehicles);
    setPlateModalStep('vehicles');
    setOpenPlateModal(true);
    setErrorPlate('');
  };

  const handlePlateSearch = async () => {
    if (!selectedCanton?.code || !plateNumber.trim()) {
      setErrorPlate("Bitte Kanton und Kennzeichen eingeben!");
      return;
    }

    setErrorPlate("");
    try {
      const response = await searchByPlate(selectedCanton.code, plateNumber.trim());
      if (isPlateDisambiguationResponse(response)) {
        setPendingPlateSearch({
          canton: selectedCanton.code,
          number: plateNumber.trim(),
        });
        setPlateDisambiguationOptions(response.options);
        setPlateModalStep('disambiguation');
        setOpenPlateModal(true);
        return;
      }

      if (Array.isArray(response) && response.length) {
        showPlateVehiclesModal(response);
      } else {
        setErrorPlate("Kennzeichen wurde nicht gefunden!");
      }
    } catch {
      setErrorPlate("Kennzeichen wurde nicht gefunden!");
    }
  };

  const handlePlateOptionClick = async (option: PlateDisambiguationOption) => {
    if (!pendingPlateSearch) {
      return;
    }

    setErrorPlate("");
    try {
      const response = await searchByPlate(
        pendingPlateSearch.canton,
        pendingPlateSearch.number,
        option.stammNr,
        option.brand
      );

      if (isPlateDisambiguationResponse(response)) {
        setPlateDisambiguationOptions(response.options);
        return;
      }

      if (Array.isArray(response) && response.length) {
        showPlateVehiclesModal(response);
      } else {
        setErrorPlate("Kennzeichen wurde nicht gefunden!");
      }
    } catch {
      setErrorPlate("Kennzeichen wurde nicht gefunden!");
    }
  };

  return (
    <div>
      <Item
        sx={{
          display: "flex",
          alignItems: "center",
          paddingBottom: "0 !important",
        }}
      >
        <img
          src="/search_plus.svg"
          alt="Fahrzeugsuchsymbol"
          style={{ width: "30px", marginRight: "8px" }} // Added margin for spacing
        />
        <Typography variant="body1" align="left" sx={{ fontSize: "13px" }}>
          Suche nach Typengenehmigung, VIN oder Motornummer
        </Typography>
      </Item>
      <Box sx={{display: "flex", justifyContent:"flex-start", padding: {xs: 0, sm: 0, md: 1}}}>
        <Box flex={1}>
        <Box
              sx={{
                backgroundColor: theme.palette.secondary.main,
                width: "40px",
                fontSize: "1.2rem",
                height: "40px",
                display: "flex",
                float: "left",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
              }}
            >
              1
            </Box>
            <TextField
              id="outlined-basic"
              label={getSearchInputLabel()}
              value={input}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setInput(event.target.value);
              }}
              sx={{
                backgroundColor: "#fff",
                borderRadius: 0,
                height: "100%",
                width: { xs: "70%", sm: "60%", md: "75%" },
                float: "left",
                top: "0px",
                transform: "translate(17px, 5px), scale(1) !important",
              }}
              InputLabelProps={{
                sx: {
                  color: theme.palette.text.primary,
                  fontSize: "13px",
                  top: '-6px',
                  transform: "translate(17px, 5px), scale(1) !important",
                },
              }}
              InputProps={{
                sx: {
                  color: theme.palette.text.primary,
                  borderRadius: 0,
                  fontSize: "13px",
                  height: "40px",
                  top: "0px",
                  transform: "translate(17px, 5px), scale(1) !important",
                },
              }}
            />
        </Box>
        <Box flex={1}>
        <Autocomplete
              disablePortal
              id="searchType"
              open={openSearchType}
              onOpen={() => {
                setOpenSearchType(true);
              }}
              onClose={() => {
                setOpenSearchType(false);
              }}
              ListboxProps={{
                style: { maxHeight: 200, overflowY: "auto" },
              }}
              PaperComponent={(props) => {
                const anchorEl = document.getElementById("searchType");
                const width = anchorEl
                  ? `${anchorEl.offsetWidth + 60}px`
                  : "auto";

                return (
                  <Popper
                    {...props}
                    open={openSearchType}
                    anchorEl={anchorEl}
                    placement="bottom-start"
                    modifiers={[
                      {
                        name: "offset",
                        options: {
                          offset: [-7, -10], // Adjust this value to move the Popper up or down
                        },
                      },
                    ]}
                    style={{
                      zIndex: 9999,
                      border: "1px solid #f1f1f1",
                      boxShadow: "2px 3px 3px #ccc",
                      position: "relative",
                      background: "#fff",
                      width: width,
                    }}
                  />
                );
              }}
              renderOption={(props, option) => {
                // Do not destructure `key` from `props`
                return (
                  <li
                    {...props}  // Spread all props directly onto the <li> element
                    key={`${option.number}`}  // Set the key prop separately
                    style={{
                      fontSize: "13px",
                      fontFamily: "Inter"
                    }}
                  >
                    {option.label}
                  </li>
                );
              }}
              
              value={selectedSearchType}
              isOptionEqualToValue={(option, value) =>
                option.label === value.label
              }
              getOptionLabel={(option) => option.label}
              options={options}
              loading={loadingSearchType}
              sx={{
                width: '100%',
                backgroundColor: "#fff",
                ".MuiInputBase-input": {
                  height: "40px",
                  marginTop: "0px",
                  marginBottom: "0px",
                  paddingTop: "0px",
                  paddingBottom: "0px",
                },
              }}
              onChange={hanldeSearchTypeChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={getSearchTypeLabel()}
                  InputLabelProps={{
                    ...params.InputLabelProps,
                    sx: {
                      color: theme.palette.text.primary,
                      fontSize: "13px",
                      height: "100%",
                      top: "-6px",
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    sx: {
                      borderRadius: 0, // Adjust the height as needed
                      fontSize: "13px",
                      fontFamily: "Inter",
                      height: '40px'
                    },
                    endAdornment: (
                      <React.Fragment>
                        {loadingSearchType ? (
                          <CircularProgress color="inherit" size={15} sx={{marginTop: '-15px'}} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            ></Autocomplete>
        </Box>
      </Box>
      <Box flex={1}>
        <Typography sx={{textAlign: "left", fontSize: "13px", fontFamily: "Inter", color:"red", ml:1}}>
          {errorVin}
        </Typography>
      </Box>
      <Box flex={1}>
        <Button
          sx={{
            width: "100%",
            mt:1,
            borderRadius: 0,
            textTransform: 'capitalize',
            backgroundColor: SEARCH_BUTTON_COLOR,
            ":hover": {
                backgroundColor: SEARCH_BUTTON_COLOR
              },
            color: "text.secondary",
          }}
          onClick={handleSearchType}
        >
          Suchen
        </Button>

        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          sx={{ zIndex: 10050 }}
        >
          <Box sx={style} onClick={(e) => e.stopPropagation()}>
            {resolvingVehicle && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={28} />
              </Box>
            )}
            <TableContainer component={Paper} sx={{ maxHeight: 340, overflow: 'auto', opacity: resolvingVehicle ? 0.5 : 1 }}>
              <Table stickyHeader sx={{ minWidth: 900 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    {vehicleModalSource === 'vin' ? (
                      <>
                        <TableCell sx={{ pb: 1, fontFamily: "Inter", fontWeight: 500 }}>Typengenehmigung</TableCell>
                        <TableCell align="left" sx={{ pb: 1, fontFamily: "Inter", fontWeight: 500 }}>Beschreibung</TableCell>
                        <TableCell align="right" sx={{ pb: 1, fontFamily: "Inter", fontWeight: 500 }}>kW</TableCell>
                        <TableCell align="right" sx={{ pb: 1, fontFamily: "Inter", fontWeight: 500 }}>PS</TableCell>
                        <TableCell align="right" sx={{ pb: 1, fontFamily: "Inter", fontWeight: 500 }}>Hubraum</TableCell>
                        <TableCell align="right" sx={{ pb: 1, fontFamily: "Inter", fontWeight: 500 }}>Bauart</TableCell>
                        <TableCell align="right" sx={{ pb: 1, fontFamily: "Inter", fontWeight: 500 }}>Antrieb</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell sx={{ pb: 1, fontFamily: "Inter", fontWeight: 500 }}>TecDoc Typ-Nr.</TableCell>
                        <TableCell align="left" sx={{ pb: 1, fontFamily: "Inter", fontWeight: 500 }}>Beschreibung</TableCell>
                        <TableCell align="right" sx={{ pb: 1, fontFamily: "Inter", fontWeight: 500 }}>Jahr</TableCell>
                        <TableCell align="right" sx={{ pb: 1, fontFamily: "Inter", fontWeight: 500 }}>kW</TableCell>
                        <TableCell align="right" sx={{ pb: 1, fontFamily: "Inter", fontWeight: 500 }}>PS</TableCell>
                        <TableCell align="right" sx={{ pb: 1, fontFamily: "Inter", fontWeight: 500 }}>Hubraum</TableCell>
                        <TableCell align="right" sx={{ pb: 1, fontFamily: "Inter", fontWeight: 500 }}>Bauart</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {linkageVehiclesByCode?.map((vehicle, index) => (
                    <TableRow
                      hover
                      key={vehicleModalSource === 'vin'
                        ? `${vehicle.typeApproval}-${index}`
                        : `${vehicle.linkageTargetId}-${index}`}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: resolvingVehicle ? 'default' : 'pointer' }}
                      onClick={() => !resolvingVehicle && handleVehicleRowClick(vehicle)}
                    >
                      {vehicleModalSource === 'vin' ? (
                        <>
                          <TableCell align="left" sx={{ fontFamily: "Inter", fontSize: '13px' }}>
                            {vehicle.typeApproval || 'N/A'}
                          </TableCell>
                          <TableCell align="left" sx={{ fontFamily: "Inter", fontSize: '13px' }}>
                            {vehicle.mfrName ? `${vehicle.mfrName} ` : ''}
                            {vehicle.description || ''}
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: "Inter", fontSize: '13px' }}>
                            {vehicle.kiloWattsTo ?? 'N/A'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: "Inter", fontSize: '13px' }}>
                            {vehicle.horsePowerTo ?? 'N/A'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: "Inter", fontSize: '13px' }}>
                            {vehicle.capacityCC ?? 'N/A'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: "Inter", fontSize: '13px' }}>
                            {vehicle.bodyStyle || 'N/A'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: "Inter", fontSize: '13px' }}>
                            {vehicle.driveType || 'N/A'}
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell align="left" sx={{ fontFamily: "Inter", fontSize: '13px' }}>
                            {vehicle?.linkageTargetId ? vehicle.linkageTargetId : 'N/A'}
                          </TableCell>
                          <TableCell align="left" sx={{ fontFamily: "Inter", fontSize: '13px' }}>
                            {vehicle?.mfrName ? vehicle.mfrName : ''}{' '}
                            {vehicle?.vehicleModelSeriesName ? vehicle.vehicleModelSeriesName : ''}{' '}
                            {vehicle?.description ? vehicle.description : ''}
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: "Inter", fontSize: '13px' }}>
                            {vehicle.beginYearMonth ? vehicle.beginYearMonth : 'N/A'}
                            -
                            {vehicle.endYearMonth ? vehicle.endYearMonth : 'N/A'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: "Inter", fontSize: '13px' }}>{vehicle.kiloWattsTo ? vehicle.kiloWattsTo : 'N/A'}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: "Inter", fontSize: '13px' }}>{vehicle.horsePowerTo ? vehicle.horsePowerTo : 'N/A'}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: "Inter", fontSize: '13px' }}>{vehicle.cylinders ? vehicle.cylinders : 'N/A'}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: "Inter", fontSize: '13px' }}>{vehicle.bodyStyle ? vehicle.bodyStyle : 'N/A'}</TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Modal>
      </Box>
      <Item
        sx={{
          display: "flex",
          alignItems: "center",
          paddingBottom: "0 !important",
        }}
      >
        <img
          src="/search_plus.svg"
          alt="Fahrzeugsuchsymbol"
          style={{ width: "30px", marginRight: "8px" }} // Added margin for spacing
        />
        <Typography variant="body1" align="left" sx={{ fontSize: "13px" }}>
          Ländercode wählen oder IT-Kennzeichen eingeben
        </Typography>
      </Item>
      <Box sx={{display: "flex", justifyContent:"flex-start", padding: {xs: 0, sm: 0, md: 1}, flexWrap: "wrap", gap: 1}}>
        <Box flex={1} sx={{ minWidth: { xs: "100%", md: "30%" } }}>
            <Box
              sx={{
                backgroundColor: theme.palette.secondary.main,
                width: "40px",
                fontSize: "1.2rem",
                height: "40px",
                display: "flex",
                float: "left",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
              }}
            >
              2
            </Box>
            <TextField
              id="searchCountry"
              label="Land"
              value="Schweiz"
              disabled
              sx={{
                backgroundColor: "#fff",
                borderRadius: 0,
                height: "100%",
                width: { xs: "70%", sm: "60%", md: "75%" },
                float: "left",
              }}
              InputLabelProps={{
                sx: {
                  color: theme.palette.text.primary,
                  fontSize: "13px",
                  top: '-6px',
                },
              }}
              InputProps={{
                sx: {
                  color: theme.palette.text.primary,
                  borderRadius: 0,
                  fontSize: "13px",
                  height: "40px",
                },
              }}
            />
          </Box>
          <Box flex={1} sx={{ minWidth: { xs: "100%", md: "30%" } }}>
            <Autocomplete
              id="searchCanton"
              open={openCantonSelect}
              onOpen={() => setOpenCantonSelect(true)}
              onClose={() => setOpenCantonSelect(false)}
              options={cantons}
              value={selectedCanton}
              onChange={(event, value) => setSelectedCanton(value)}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              getOptionLabel={(option) => option.label}
              sx={{
                width: "100%",
                backgroundColor: "#fff",
                ".MuiInputBase-input": {
                  height: "40px",
                  marginTop: "-15px",
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Kanton"
                  InputLabelProps={{
                    sx: {
                      color: theme.palette.text.primary,
                      fontSize: "13px",
                      top: "-6px",
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    sx: {
                      borderRadius: 0,
                      height: "40px",
                      fontSize: "13px",
                    },
                  }}
                />
              )}
            />
          </Box>
          <Box flex={1} sx={{ minWidth: { xs: "100%", md: "30%" } }}>
            <TextField
              id="plateNumber"
              label="Kennzeichen eingeben"
              value={plateNumber}
              onChange={(event) => setPlateNumber(event.target.value)}
              sx={{
                backgroundColor: "#fff",
                borderRadius: 0,
                height: "100%",
                width: "100%",
              }}
              InputLabelProps={{
                sx: {
                  color: theme.palette.text.primary,
                  fontSize: "13px",
                  top: '-6px',
                },
              }}
              InputProps={{
                sx: {
                  color: theme.palette.text.primary,
                  borderRadius: 0,
                  fontSize: "13px",
                  height: "40px",
                },
              }}
            />
          </Box>
        </Box>
        <Box flex={1}>
        <Typography sx={{textAlign: "left", fontSize: "13px", fontFamily: "Inter", color:"red", ml:1}}>
          {errorPlate}
        </Typography>
        </Box>
        <Box flex={1}>
        <Button
          sx={{
            width: "100%",
            mt:1,
            borderRadius: 0,
            textTransform: 'capitalize',
            backgroundColor: SEARCH_BUTTON_COLOR,
            ":hover": {
                backgroundColor: SEARCH_BUTTON_COLOR
              },
            color: "text.secondary",
          }}
          onClick={handlePlateSearch}
        >
          Suchen
        </Button>
      </Box>

      <Modal
        open={openPlateModal}
        onClose={handleClosePlateModal}
        sx={{ zIndex: 10050 }}
      >
        <Box sx={{ ...style, height: 'auto', maxHeight: '70vh' }} onClick={(e) => e.stopPropagation()}>
          {plateModalStep === 'disambiguation' ? (
            <>
              <Typography sx={{ mb: 2, fontFamily: "Inter", fontSize: '14px', fontWeight: 600 }}>
                Fahrzeug auswählen
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 340, overflow: 'auto' }}>
                <Table stickyHeader sx={{ minWidth: 600 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontFamily: "Inter", fontWeight: 500 }}>Marke</TableCell>
                      <TableCell sx={{ fontFamily: "Inter", fontWeight: 500 }}>Fahrzeugart</TableCell>
                      <TableCell sx={{ fontFamily: "Inter", fontWeight: 500 }}>Platte</TableCell>
                      <TableCell sx={{ fontFamily: "Inter", fontWeight: 500 }}>Art</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {plateDisambiguationOptions.map((option) => (
                      <TableRow
                        hover
                        key={`${option.stammNr}-${option.brand}`}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handlePlateOptionClick(option)}
                      >
                        <TableCell sx={{ fontFamily: "Inter", fontSize: '13px' }}>{option.brand}</TableCell>
                        <TableCell sx={{ fontFamily: "Inter", fontSize: '13px' }}>{option.vehicleType || 'N/A'}</TableCell>
                        <TableCell sx={{ fontFamily: "Inter", fontSize: '13px' }}>{option.plateColor || 'N/A'}</TableCell>
                        <TableCell sx={{ fontFamily: "Inter", fontSize: '13px' }}>{option.kind || 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <>
              {resolvingVehicle && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={28} />
                </Box>
              )}
              <Typography sx={{ mb: 2, fontFamily: "Inter", fontSize: '14px', fontWeight: 600 }}>
                Fahrzeug auswählen
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 340, overflow: 'auto', opacity: resolvingVehicle ? 0.5 : 1 }}>
                <Table stickyHeader sx={{ minWidth: 1000 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontFamily: "Inter", fontWeight: 500 }}>Typengenehmigung</TableCell>
                      <TableCell sx={{ fontFamily: "Inter", fontWeight: 500 }}>Beschreibung</TableCell>
                      <TableCell align="right" sx={{ fontFamily: "Inter", fontWeight: 500 }}>kW</TableCell>
                      <TableCell align="right" sx={{ fontFamily: "Inter", fontWeight: 500 }}>PS</TableCell>
                      <TableCell align="right" sx={{ fontFamily: "Inter", fontWeight: 500 }}>Hubraum</TableCell>
                      <TableCell align="right" sx={{ fontFamily: "Inter", fontWeight: 500 }}>Bauart</TableCell>
                      <TableCell align="right" sx={{ fontFamily: "Inter", fontWeight: 500 }}>Kraftstoff</TableCell>
                      <TableCell align="right" sx={{ fontFamily: "Inter", fontWeight: 500 }}>Antrieb</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {plateModalVehicles.map((vehicle, index) => (
                      <TableRow
                        hover
                        key={`${vehicle.typeApproval}-${index}`}
                        sx={{ cursor: resolvingVehicle ? 'default' : 'pointer' }}
                        onClick={() => !resolvingVehicle && handlePlateVehicleRowClick(vehicle)}
                      >
                        <TableCell sx={{ fontFamily: "Inter", fontSize: '13px' }}>{vehicle.typeApproval || 'N/A'}</TableCell>
                        <TableCell sx={{ fontFamily: "Inter", fontSize: '13px' }}>
                          {vehicle.mfrName ? `${vehicle.mfrName} ` : ''}
                          {vehicle.description || ''}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: "Inter", fontSize: '13px' }}>{vehicle.kiloWattsTo ?? 'N/A'}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: "Inter", fontSize: '13px' }}>{vehicle.horsePowerTo ?? 'N/A'}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: "Inter", fontSize: '13px' }}>{vehicle.capacityCC ?? 'N/A'}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: "Inter", fontSize: '13px' }}>{vehicle.bodyStyle || 'N/A'}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: "Inter", fontSize: '13px' }}>{vehicle.fuelType || 'N/A'}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: "Inter", fontSize: '13px' }}>{vehicle.driveType || 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      </Modal>
    </div>
  );
};

// Top 100 films as rated by IMDb users. http://www.imdb.com/chart/top
const searchTypes = [
  { label: " Typengenehmigung", number: 3 },
  { label: " VIN", number: 1 },
  { label: " Motornummer", number: 2 },
];

const cantons: CantonOption[] = [
  "AG", "AI", "AR", "BE", "BL", "BS", "BU", "FL", "FR", "GE", "GL", "GR", "JU", "LU", "M",
  "NE", "MW", "OW", "SG", "SH", "SO", "SZ", "TG", "TI", "UR", "VD", "VS", "ZG", "ZH",
].map((code) => ({ code, label: code }));

export default VehicleIdentification;
